import React, { useState } from "react";
import { Twitter, Linkedin, Mail, BookOpen, Coffee, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/catalog";

export default function Footer({ author, subscribeRef, onEditor, editorActive }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const s = author?.socials || {};

  const submit = async (e) => {
    e.preventDefault();
    const addr = email.trim();
    if (!addr) return;
    setLoading(true);
    try {
      const res = await api.subscribe(addr);
      if (res.substack) {
        toast.success(res.message || "You're on the Lapils list.");
      } else {
        // Substack blocks server-side signups — finish it on Substack's own page so it truly lands.
        const base = (s.substack || "https://thepebble.substack.com/").replace(/\/$/, "");
        window.open(`${base}/subscribe?email=${encodeURIComponent(addr)}`, "_blank", "noopener,noreferrer");
        toast.success("Opening Substack to confirm your subscription…");
      }
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Enter a valid email address.");
    } finally {
      setLoading(false);
    }
  };

  const socials = [
    { icon: Twitter, label: "X", url: s.twitter },
    { icon: Linkedin, label: "LinkedIn", url: s.linkedin },
    { icon: BookOpen, label: "Substack", url: s.substack },
    { icon: Mail, label: "Email", url: s.email },
    { icon: Coffee, label: "Coffee", url: s.coffee },
  ].filter((x) => x.url);

  return (
    <footer className="border-t border-[var(--line)]">
      {/* Subscribe */}
      <div ref={subscribeRef} id="subscribe" className="scroll-mt-20 max-w-[1320px] mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <div className="relative bg-[var(--surface)] border border-[var(--line)] p-8 sm:p-12 overflow-hidden">
          <span className="absolute top-0 left-0 w-16 h-[2px] bg-[var(--gold)]" />
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="meta-label text-[var(--gold)]">Subscribe · The Pebble</p>
              <h3 className="font-serif text-3xl sm:text-4xl mt-3 text-[var(--txt)] leading-tight">
                Get the next brief in your inbox.
              </h3>
              <p className="mt-3 text-sm text-[var(--muted)] max-w-md leading-relaxed">
                7-minute perspectives on moral psychology, comparative mythology and geopolitics.
              </p>
            </div>
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
              <input
                data-testid="subscribe-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-[var(--ink)] border border-[var(--line)] focus:border-[var(--gold)] outline-none px-4 py-3.5 text-sm text-[var(--txt)] placeholder:text-[var(--muted)] transition-colors"
              />
              <button
                data-testid="subscribe-submit-btn"
                type="submit"
                disabled={loading}
                className="flex items-center justify-center gap-2 px-6 py-3.5 meta-label transition-all disabled:opacity-60"
                style={{ backgroundColor: "var(--gold)", color: "var(--ink)" }}
              >
                {loading ? <Loader2 size={15} className="animate-spin" /> : "Subscribe"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[var(--line)]">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-8 h-8 border font-serif text-sm"
              style={{ borderColor: "var(--gold)", color: "var(--gold)" }}>DL</span>
            <span className="meta-label text-[var(--muted)] text-[0.56rem]">
              © {new Date().getFullYear()} Dre Lapiello · Written works index
            </span>
            <button data-testid="editor-access-btn" onClick={onEditor}
              className="meta-label text-[0.52rem] px-2 py-1 border border-[var(--line)] transition-colors"
              style={{ color: editorActive ? "var(--gold)" : "var(--muted)", borderColor: editorActive ? "var(--gold)" : "var(--line)" }}>
              {editorActive ? "Editor ✓" : "Editor"}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {socials.map((soc) => {
              const Icon = soc.icon;
              return (
                <a key={soc.label} data-testid={`social-${soc.label}`} href={soc.url} target="_blank" rel="noopener noreferrer"
                  aria-label={soc.label}
                  className="grid place-items-center w-9 h-9 border border-[var(--line)] text-[var(--txt-2)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
                  <Icon size={15} />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}
